import * as React from 'react';
import { useContext } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import FeatureFlagContext from '@/src/Features/components/FeatureFlagContext';
import { WEB_V3_FLAG } from '../flags';
import { useWorld } from './UnifiedFrame';

/**
 * Renders a v3-only page in the v3 world; in the classic world it sends the
 * visitor to the page that existed before (`fallback`, `:param` filled from
 * the route), so no v3 shell ever appears inside the classic header.
 */
export default function V3Only({ fallback, children }: { fallback: string; children: React.ReactElement }) {
    const flags = useContext(FeatureFlagContext) as Record<string, boolean>;
    const v3 = useWorld(!!flags?.[WEB_V3_FLAG]);
    const params = useParams();
    if (v3) return children;
    const to = fallback.replace(/:([A-Za-z]+)/g, (_, k) => params[k] ?? '');
    return <Navigate to={to} replace />;
}

/**
 * The reverse: a classic page that a newer page replaces. In the v3 world it
 * redirects to `to` (`:param` filled from the route); in classic it renders as before.
 */
export function V3Redirect({ to, children }: { to: string; children: React.ReactElement }) {
    const flags = useContext(FeatureFlagContext) as Record<string, boolean>;
    const v3 = useWorld(!!flags?.[WEB_V3_FLAG]);
    const params = useParams();
    if (!v3) return children;
    const target = to.replace(/:([A-Za-z]+)/g, (_, k) => params[k] ?? '');
    return <Navigate to={target} replace />;
}

/** Picks the classic or the v3 version of a page by world (for routes both share). */
export function WorldPick({ classic, v3 }: { classic: React.ReactElement; v3: React.ReactElement }) {
    const flags = useContext(FeatureFlagContext) as Record<string, boolean>;
    return useWorld(!!flags?.[WEB_V3_FLAG]) ? v3 : classic;
}
